<template>
  <div
    class="table-control-bar tw-flex tw-flex-col md:tw-flex-row tw-justify-between tw-gap-1"
    :class="{ fullScreenMode }"
  >
    <WdsStack gap-sm align-center class="search-filter-input md:tw-max-w-[400px]">
      <WdsInput
        v-if="searchArg"
        v-model="search"
        :placeholder="searchPlaceholder"
        :title="searchPlaceholder ?? ''"
        :prefix-icon="WdsIconNameEnum.SEARCH"
        data-test="graphql-data-table-search-input"
      />
      <div v-if="!hideFilters" class="tw-my-auto tw-px-3 table-filter-button">
        <WdsStack gap-xs align-center>
          <WdsButtonGhost
            class="tw-h-7.5s"
            data-test="table-filter-button"
            @click="showFilters = !showFilters"
          >
            <WdsStack gap-sm align-center>
              <WdsIcon name="settings-adjust" :size="16" weight="thin" />
              {{ $t('Filters') }}
            </WdsStack>
          </WdsButtonGhost>
          <WdsButton
            v-show="numActiveFilters > 0"
            class="secondary-proper"
            rounded
            :title="$t('Reset filters')"
            sm
            @click="emit('reset-filters')"
          >
            <WdsStack gap-xs align-center>
              <span>{{ numActiveFilters }}</span>
              <WdsIcon name="close" :size="16" />
            </WdsStack>
          </WdsButton>
        </WdsStack>
      </div>
    </WdsStack>
    <WdsStack gap-sm align-center>
      <slot name="filters"></slot>
      <div v-if="$slots.filters" class="v-split"></div>
      <WdsText :text="$tc(':count item | :count items', numberOfItems)" />
      <template v-if="orderByArg.length > 0">
        <div class="v-split"></div>
        <DataTableSort v-model:sort="sort" :sort-options="orderByArg" />
      </template>
      <div class="v-split"></div>
      <GraphqlDataTableColumnManager
        :query="stringifiedQuery"
        :allow-cross-table-columns
        :column-black-list="columnBlackList"
      />
      <div class="v-split"></div>
      <WdsButtonGhost
        :title="$t('Download table')"
        :aria-label="$t('Download table')"
        @click="$emit('download')"
      >
        <WdsIcon name="download" :size="18" />
      </WdsButtonGhost>
      <div class="v-split"></div>
      <WdsButtonGhost
        :title="$t('Fullscreen')"
        :aria-label="$t('Fullscreen')"
        @click="toggleFullScreen"
      >
        <WdsIcon v-if="!fullScreenMode" name="maximize" :size="18" />
        <WdsIcon v-else name="minimize" :size="18" />
      </WdsButtonGhost>
    </WdsStack>
  </div>
</template>

<script lang="ts" setup>
import {
  WdsButtonGhost,
  WdsIcon,
  WdsInput,
  WdsStack,
  WdsText,
  WdsIconNameEnum,
  WdsButton,
} from '@worksome/wds-components'
import { print } from 'graphql'
import { computed, ref, watch } from 'vue'
import { useOrderingType, useQueryType } from '@/components/GraphqlDataTable/composables'
import { $t, $tc } from '@/composables/translation'
import DataTableSort from '@/components/BaseDataTable/DataTableSort.vue'
import { generateSortOptions } from '@/components/GraphqlDataTable/tools'
import { SortOption } from '@/components/BaseDataTable/composables/dataTableSort'
import { SortOrder } from '@/gql/graphql'
import columns from '@/components/GraphqlDataTable/columns'
import { DataTableControlBarProps } from '@/components/GraphqlDataTable/types'
import GraphqlDataTableColumnManager from '@/components/GraphqlDataTable/GraphqlDataTableColumnManager.vue'

const emit = defineEmits(['sort-settings', 'download', 'full-screen', 'reset-filters'])

const props = defineProps<DataTableControlBarProps>()
const stringifiedQuery = computed(() => print(props.query))

const { args: queryArgs } = useQueryType(stringifiedQuery)

const searchArg = computed(() =>
  queryArgs.value.inputtedArgs.filter((arg) => !!arg).find(({ name }) => name === 'search'),
)

const searchPlaceholder = computed(() => props.searchPlaceholder || searchArg.value?.description)

const toggleFullScreen = () => {
  emit('full-screen', !props.fullScreenMode)
}

const existingSorts = computed(() =>
  Object.values(columns)
    .filter(({ sort }) => !!sort)
    .flatMap(({ sort }) => sort)
    .reduce((acc, sort) => ({ ...acc, [sort?.name ?? 'no-name']: sort }), {}),
)

const { orderType } = useOrderingType(stringifiedQuery)

const orderByArg = computed(() => {
  if (props.sortOptions && props.sortOptions.length > 0) {
    return props.sortOptions
  }

  if (orderType.value === null || !orderType.value.enumValues) return []
  const generatedSortOptions: SortOption[] = orderType.value.enumValues.flatMap((enumValue) =>
    generateSortOptions(enumValue, existingSorts),
  )

  // Sort order text
  // Date type, New to old | Old to new
  // String type, A-Z | Z-A
  // Numeric type, More - Less | Less - More
  return generatedSortOptions
})

const search = defineModel<string>('search')
const showFilters = defineModel<boolean>('showFilters')
const sort = ref<SortOption | undefined>(undefined)
const orderBy = defineModel<{ field: string; order: SortOrder | null } | undefined>('orderBy')

watch(sort, () => {
  if (!sort.value) {
    orderBy.value = undefined
    return
  }

  // Convert the sort value to a type that supports the input value for our query
  orderBy.value = {
    field: sort.value.orderByColumn,
    order: sort.value.sortOrder,
  }
})

watch([orderBy, orderByArg], () => {
  sort.value = orderByArg.value.find(
    (option) =>
      option.orderByColumn === orderBy.value?.field && orderBy.value?.order === option.sortOrder,
  )
})
</script>

<style lang="scss" scoped>
.fullScreenMode {
  padding: 20px;
}

.v-split {
  border-left: 1px solid #c1c1cb;
  height: 16px;
}

.table-control-bar {
  &:deep(.wds-input-wrapper) {
    input {
      text-overflow: ellipsis;
    }
  }
}

.table-filter-button {
  &:deep(.wds-icon) {
    path {
      stroke: currentColor;
      stroke-width: 1px;
    }
  }
}

.search-filter-input {
  width: 100%;
  :deep(.wds-input-wrapper) {
    width: 100%;
  }
  &:deep(.wds-input-label) {
    margin: 0;
  }
}
.secondary-proper {
  @apply tw-bg-indigo-500;
  @apply tw-text-white;
  &:hover {
    @apply tw-bg-indigo-400;
  }
}
</style>
